/**
 * Define programs (projects) and portfolios (programs) for the histogram.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import { catalogMatchesSearch, catalogRowKey, catalogSubtitle, catalogTitle, resolveProgramMembers } from '../../utils/fabHistogramCatalog';

const btn = {
  padding: '6px 12px',
  fontSize: '0.8rem',
  border: '1px solid #d1d5db',
  borderRadius: 6,
  background: 'white',
  color: '#334155',
  cursor: 'pointer',
};

function catalogKey(row) {
  return catalogRowKey(row);
}

function membersFromKeys(keys, catalog, orphans = []) {
  const want = new Set(keys);
  const out = [];
  const seen = new Set();
  const push = (member) => {
    const projectKey = String(member.projectKey || '').trim();
    if (!projectKey) return;
    const stamp = `${projectKey}|${member.jobNumber || ''}|${member.estimateCode || ''}`;
    if (seen.has(stamp)) return;
    seen.add(stamp);
    out.push({
      projectKey,
      jobNumber: member.jobNumber || null,
      estimateCode: member.estimateCode || null,
    });
  };
  catalog.forEach((row) => {
    if (!want.has(catalogKey(row))) return;
    push({
      projectKey: row.project,
      jobNumber: row.jobNumber || null,
      estimateCode: row.estimateCode || null,
    });
  });
  orphans.forEach((row) => {
    if (!want.has(row.key)) return;
    push(row.member || {});
  });
  return out;
}

function isPortfolio(row) {
  return String(row?.layer || '') === 'portfolio';
}

export default function FabricationHistogramPortfoliosModal({
  onClose,
  portfolios = [],
  catalog = [],
  dataSource = 'p6',
  onChanged,
}) {
  const { instance, accounts } = useMsal();
  const [layer, setLayer] = useState('program');
  const [name, setName] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [picked, setPicked] = useState(() => new Set());
  const [orphans, setOrphans] = useState([]);
  const [pickedPrograms, setPickedPrograms] = useState(() => new Set());
  const editingMembersRef = useRef([]);
  const pickedTouchedRef = useRef(false);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [liveCatalog, setLiveCatalog] = useState(catalog);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogLive, setCatalogLive] = useState(null);
  const [catalogError, setCatalogError] = useState('');

  const programs = useMemo(() => (portfolios || []).filter((g) => !isPortfolio(g)), [portfolios]);
  const portfolioRows = useMemo(() => (portfolios || []).filter(isPortfolio), [portfolios]);
  const p6Source = dataSource === 'p6';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setCatalogLoading(true);
      setCatalogError('');
      try {
        const catalogRes = await authenticatedApiCall(
          instance,
          accounts,
          'GET',
          `/fab-histogram/portfolios/catalog?source=${encodeURIComponent(dataSource)}`,
          null,
          { timeout: 120000 }
        );
        if (cancelled) return;
        if (catalogRes?.data?.success) {
          setLiveCatalog(catalogRes.data.catalog || []);
          setCatalogLive(catalogRes.data.live !== false && dataSource === 'p6');
          if (catalogRes.data.message && catalogRes.data.live === false) {
            setCatalogError(catalogRes.data.message);
          }
        } else {
          setCatalogLive(false);
          setCatalogError(catalogRes?.data?.message || 'Could not read P6 XERs');
        }
      } catch (err) {
        if (!cancelled) {
          setCatalogLive(false);
          setCatalogError(err?.response?.data?.message || err.message || 'Could not read P6 XERs');
        }
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [instance, accounts, dataSource]);

  const filteredCatalog = useMemo(() => {
    const q = search.trim();
    if (!q) return liveCatalog;
    return liveCatalog.filter((row) => catalogMatchesSearch(row, q));
  }, [liveCatalog, search]);

  const filteredPrograms = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return programs;
    return programs.filter((p) => (
      [p.name, ...(p.members || []).map((m) => [m.projectKey, m.jobNumber, m.estimateCode].filter(Boolean).join(' '))]
        .join(' ')
        .toLowerCase()
        .includes(q)
    ));
  }, [programs, search]);

  const resetForm = (nextLayer = layer) => {
    setLayer(nextLayer);
    setName('');
    setEditingId(null);
    editingMembersRef.current = [];
    pickedTouchedRef.current = false;
    setPicked(new Set());
    setOrphans([]);
    setPickedPrograms(new Set());
    setSearch('');
    setError('');
  };

  const startEdit = (row) => {
    const nextLayer = isPortfolio(row) ? 'portfolio' : 'program';
    setLayer(nextLayer);
    setEditingId(row.id);
    setName(row.name || '');
    setError('');
    setSearch('');
    if (nextLayer === 'portfolio') {
      setPicked(new Set());
      setPickedPrograms(new Set((row.childProgramIds || []).map(String)));
      return;
    }
    setPickedPrograms(new Set());
    editingMembersRef.current = row.members || [];
    pickedTouchedRef.current = false;
    const resolved = resolveProgramMembers(row.members, liveCatalog);
    setPicked(resolved.keys);
    setOrphans(resolved.orphans);
  };

  useEffect(() => {
    if (!editingId || layer !== 'program' || pickedTouchedRef.current) return;
    const resolved = resolveProgramMembers(editingMembersRef.current, liveCatalog);
    setPicked(resolved.keys);
    setOrphans(resolved.orphans);
  }, [liveCatalog, editingId, layer]);

  const save = async () => {
    if (!name.trim()) {
      setError(layer === 'portfolio'
        ? 'Name the portfolio — for example Canada.'
        : 'Name the program — for example Goodyear.');
      return;
    }
    const payload = { name: name.trim(), layer };
    if (layer === 'portfolio') {
      payload.childProgramIds = [...pickedPrograms].map(Number).filter((n) => Number.isFinite(n));
      if (!payload.childProgramIds.length) {
        setError('Check at least one program.');
        return;
      }
    } else {
      payload.members = membersFromKeys(picked, liveCatalog, orphans);
      if (!payload.members.length) {
        setError(p6Source ? 'Check at least one P6 XER.' : 'Check at least one project.');
        return;
      }
    }
    setSaving(true);
    setError('');
    try {
      const path = editingId ? `/fab-histogram/portfolios/${editingId}` : '/fab-histogram/portfolios';
      const method = editingId ? 'PUT' : 'POST';
      const res = await authenticatedApiCall(instance, accounts, method, path, payload);
      if (!res?.data?.success) throw new Error(res?.data?.message || 'Save failed');
      resetForm(layer);
      onChanged?.();
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    const kind = isPortfolio(row) ? 'portfolio' : 'program';
    if (!window.confirm(`Delete ${kind} “${row.name}”?`)) return;
    try {
      await authenticatedApiCall(instance, accounts, 'DELETE', `/fab-histogram/portfolios/${row.id}`);
      if (editingId === row.id) resetForm();
      onChanged?.();
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Delete failed');
    }
  };

  const toggle = (key) => {
    pickedTouchedRef.current = true;
    const next = new Set(picked);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setPicked(next);
  };

  const toggleProgram = (id) => {
    const key = String(id);
    const next = new Set(pickedPrograms);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setPickedPrograms(next);
  };

  const inProgramRows = useMemo(() => {
    const rows = [];
    orphans.forEach((row) => {
      if (picked.has(row.key)) rows.push({ kind: 'orphan', key: row.key, row });
    });
    liveCatalog.forEach((row) => {
      const key = catalogKey(row);
      if (picked.has(key)) rows.push({ kind: 'catalog', key, row });
    });
    return rows;
  }, [orphans, liveCatalog, picked]);
  const inProgramKeys = useMemo(() => new Set(inProgramRows.map((row) => row.key)), [inProgramRows]);

  const noun = layer === 'portfolio' ? 'portfolio' : 'program';

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={onClose}
    >
      <div
        style={{ width: 'min(960px, 100%)', height: 'min(860px, 90vh)', background: 'white', borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: '#0f766e', color: 'white', flex: 'none' }}>
          <h2 style={{ margin: 0, fontSize: 18 }}>Define programs / portfolios</h2>
          <span style={{ fontSize: 12, opacity: 0.85 }}>
            Two layers above Project: a program is jobs (Goodyear). A portfolio is programs (Canada).
          </span>
          <button type="button" onClick={onClose} style={{ marginLeft: 'auto', ...btn, borderColor: 'transparent', background: 'rgba(255,255,255,0.14)', color: 'white' }}>Close</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '260px minmax(0, 1fr)', flex: 1, minHeight: 0 }}>
          <div style={{ borderRight: '1px solid #e2e8f0', overflowY: 'auto', minHeight: 0, padding: 12 }}>
            <button type="button" style={{ ...btn, width: '100%', marginBottom: 6, background: '#f0fdfa', borderColor: '#99f6e4', color: '#0f766e', fontWeight: 700 }} onClick={() => resetForm('program')}>
              New program
            </button>
            <button type="button" style={{ ...btn, width: '100%', marginBottom: 10 }} onClick={() => resetForm('portfolio')}>
              New portfolio
            </button>
            <SavedList title="Programs" rows={programs} empty="None yet. Name a program and check P6 XERs." onEdit={startEdit} onRemove={remove} />
            <SavedList title="Portfolios" rows={portfolioRows} empty="None yet. A portfolio picks programs." onEdit={startEdit} onRemove={remove} countLabel={(row) => `${(row.childProgramIds || []).length} program${(row.childProgramIds || []).length === 1 ? '' : 's'}`} />
          </div>
          <div style={{ overflow: 'hidden', minHeight: 0, padding: 14, display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              {[['program', 'Program — projects'], ['portfolio', 'Portfolio — programs']].map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  disabled={Boolean(editingId) && id !== layer}
                  onClick={() => { if (!editingId) resetForm(id); }}
                  style={{
                    ...btn,
                    fontWeight: 700,
                    background: layer === id ? '#0f766e' : '#fff',
                    color: layer === id ? '#fff' : '#334155',
                    borderColor: layer === id ? '#0f766e' : '#d1d5db',
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
            <label style={{ display: 'block', fontSize: 12, color: '#475569', marginBottom: 10 }}>
              {layer === 'portfolio' ? 'Portfolio name' : 'Program name'}
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={layer === 'portfolio' ? 'Canada' : 'Goodyear'}
                style={{ ...btn, display: 'block', width: '100%', marginTop: 4, boxSizing: 'border-box' }}
              />
            </label>
            <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>
              {catalogLoading
                ? (p6Source ? 'Reading every P6 XER…' : 'Reading projects…')
                : (p6Source
                  ? (catalogLive
                    ? `${liveCatalog.length.toLocaleString('en-CA')} P6 XERs`
                    : `${liveCatalog.length.toLocaleString('en-CA')} Refresh copies (live P6 list did not load)`)
                  : `${liveCatalog.length.toLocaleString('en-CA')} projects`)}
              {search.trim() && !catalogLoading ? ` · ${filteredCatalog.length.toLocaleString('en-CA')} match` : ''}
            </div>
            {catalogError && (
              <div style={{ marginBottom: 8, color: '#be123c', fontSize: 12 }}>{catalogError}</div>
            )}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={layer === 'portfolio' ? 'Search programs…' : (p6Source ? 'Search P6 XERs…' : 'Search projects…')}
                style={{ ...btn, flex: 1 }}
              />
              {layer === 'portfolio' ? (
                <>
                  <button
                    type="button"
                    style={btn}
                    onClick={() => setPickedPrograms(new Set([...pickedPrograms, ...filteredPrograms.map((p) => String(p.id))]))}
                    disabled={!filteredPrograms.length}
                  >
                    Select matching
                  </button>
                  <button type="button" style={btn} onClick={() => setPickedPrograms(new Set())} disabled={!pickedPrograms.size}>Clear</button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    style={btn}
                    onClick={() => {
                      pickedTouchedRef.current = true;
                      setPicked(new Set([...picked, ...filteredCatalog.map(catalogKey)]));
                    }}
                    disabled={!filteredCatalog.length}
                  >
                    Select matching
                  </button>
                  <button type="button" style={btn} onClick={() => { pickedTouchedRef.current = true; setPicked(new Set()); }} disabled={!picked.size}>Clear</button>
                </>
              )}
            </div>
            <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, flex: 1, minHeight: 0, overflowY: 'auto' }}>
              {layer === 'portfolio' ? (
                <>
                  {filteredPrograms.map((row) => (
                    <label key={row.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '7px 10px', fontSize: 13, borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}>
                      <input type="checkbox" checked={pickedPrograms.has(String(row.id))} onChange={() => toggleProgram(row.id)} />
                      <span>
                        <span style={{ color: '#0f172a' }}>{row.name}</span>
                        <span style={{ display: 'block', fontSize: 11, color: '#64748b' }}>
                          {(row.members || []).length} project{(row.members || []).length === 1 ? '' : 's'}
                        </span>
                      </span>
                    </label>
                  ))}
                  {!filteredPrograms.length && (
                    <div style={{ padding: 14, fontSize: 13, color: '#94a3b8' }}>
                      {programs.length ? 'No programs match that search.' : 'Create a program first, then add it to a portfolio.'}
                    </div>
                  )}
                </>
              ) : (
                <>
                  {inProgramRows.length > 0 && (
                    <div style={{ padding: '6px 10px', fontSize: 11, fontWeight: 700, color: '#0f766e', background: '#f0fdfa' }}>
                      In this program
                    </div>
                  )}
                  {inProgramRows.map((item) => {
                    if (item.kind === 'orphan') {
                      const row = item.row;
                      return (
                        <label key={row.key} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '7px 10px', fontSize: 13, borderBottom: '1px solid #f1f5f9', cursor: 'pointer', background: '#f0fdfa' }}>
                          <input type="checkbox" checked onChange={() => toggle(row.key)} />
                          <span>
                            <span style={{ color: '#0f172a' }}>{row.title}</span>
                            <span style={{ display: 'block', fontSize: 11, color: '#64748b' }}>{row.subtitle}</span>
                          </span>
                        </label>
                      );
                    }
                    const row = item.row;
                    return (
                      <label key={`in-${item.key}`} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '7px 10px', fontSize: 13, borderBottom: '1px solid #f1f5f9', cursor: 'pointer', background: '#f0fdfa' }}>
                        <input type="checkbox" checked onChange={() => toggle(item.key)} />
                        <span>
                          <span style={{ color: '#0f172a' }}>{catalogTitle(row)}</span>
                          <span style={{ display: 'block', fontSize: 11, color: '#64748b' }}>
                            {catalogSubtitle(row) || (p6Source ? 'P6 XER' : 'No job / estimate on this row')}
                          </span>
                        </span>
                      </label>
                    );
                  })}
                  {filteredCatalog.filter((row) => !inProgramKeys.has(catalogKey(row))).map((row) => {
                    const key = catalogKey(row);
                    return (
                      <label key={key} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '7px 10px', fontSize: 13, borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}>
                        <input type="checkbox" checked={picked.has(key)} onChange={() => toggle(key)} />
                        <span>
                          <span style={{ color: '#0f172a' }}>{catalogTitle(row)}</span>
                          <span style={{ display: 'block', fontSize: 11, color: '#64748b' }}>
                            {catalogSubtitle(row) || (p6Source ? 'P6 XER' : 'No job / estimate on this row')}
                          </span>
                        </span>
                      </label>
                    );
                  })}
                  {!filteredCatalog.length && (
                    <div style={{ padding: 14, fontSize: 13, color: '#94a3b8' }}>
                      {catalogLoading
                        ? 'Reading P6 XERs…'
                        : liveCatalog.length
                          ? 'No XERs match that search.'
                          : (p6Source ? 'No P6 projects returned.' : 'No projects on this source yet. Import Excel first.')}
                    </div>
                  )}
                </>
              )}
            </div>
            {error && <div style={{ marginTop: 10, color: '#be123c', fontSize: 13, flex: 'none' }}>{error}</div>}
            <div style={{ display: 'flex', gap: 8, marginTop: 12, flex: 'none' }}>
              <button
                type="button"
                onClick={save}
                disabled={saving}
                style={{ ...btn, background: '#0f766e', color: 'white', borderColor: '#0f766e', fontWeight: 700 }}
              >
                {saving ? 'Saving…' : editingId ? `Save ${noun}` : `Create ${noun}`}
              </button>
              {editingId && <button type="button" style={btn} onClick={() => resetForm(layer)}>Cancel edit</button>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SavedList({ title, rows, empty, onEdit, onRemove, countLabel }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 6 }}>{title}</div>
      {rows.map((p) => (
        <div key={p.id} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 8, marginBottom: 8 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#0f172a' }}>{p.name}</div>
          <div style={{ fontSize: 11, color: '#64748b', margin: '2px 0 6px' }}>
            {countLabel ? countLabel(p) : `${(p.members || []).length} project${(p.members || []).length === 1 ? '' : 's'}`}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button type="button" style={btn} onClick={() => onEdit(p)}>Edit</button>
            <button type="button" style={{ ...btn, color: '#be123c' }} onClick={() => onRemove(p)}>Delete</button>
          </div>
        </div>
      ))}
      {!rows.length && <div style={{ fontSize: 12, color: '#94a3b8' }}>{empty}</div>}
    </div>
  );
}
